// Async cursor-paged list primitives. Pure TS so both web and mobile
// can build their hooks on top.
//
// Why a shared util: the cursor loop and the cache-adapter contract
// are identical across surfaces; only the storage backend differs
// (IDB on web, AsyncStorage on mobile). Centralising the shape keeps
// the two implementations from drifting and makes it obvious that the
// pagination contract belongs to the API, not the UI.
//
// Throttling: every page fetch goes through the caller's apiFetch,
// which already retries 429s with backoff and idempotency-keys
// non-GETs. paginate() doesn't add a second retry layer — that would
// double the back-off arc and risk amplifying the very throttle we're
// trying to ride out. Aborts are propagated via the signal.
//
// We don't depend on the DOM AbortSignal type here because the shared
// package's tsconfig deliberately omits the DOM lib (it's consumed by
// both browser and Hermes targets). A structural `AbortLike` is
// satisfied by `new AbortController().signal` in every modern runtime.

export type AbortLike = { readonly aborted: boolean };

export type Page<T, C = string> = {
  items: T[];
  /** null when there are no more pages. */
  cursor: C | null;
};

/** One fetch in the cursor loop. cursor=null means "first page". */
export type FetchPage<T, C = string> = (
  cursor: C | null,
  signal?: AbortLike,
) => Promise<Page<T, C>>;

/** Platform-agnostic cache adapter. Mobile binds it to AsyncStorage,
 *  web to IndexedDB. The shape is intentionally minimal — read on
 *  hydrate, write on refresh — because anything richer (TTL, partial
 *  invalidation) leaks platform-specific concerns up. */
export type ListCache<T> = {
  read: () => Promise<ReadonlyArray<T> | null>;
  write: (items: ReadonlyArray<T>) => Promise<void>;
};

export type PaginateOptions<T, C = string> = {
  /** Emitted after each page so the caller can render progressively. */
  onPage?: (page: Page<T, C>) => void;
  /** Abort propagates to fetchPage(signal) AND interrupts the loop
   *  between pages. */
  signal?: AbortLike;
  /** Safety net for runaway servers. Defaults to 200 pages — at the
   *  current 50-item page size that's ten thousand items, well above
   *  the family-scale ceiling we operate at. */
  maxPages?: number;
};

const DEFAULT_MAX_PAGES = 200;

/**
 * Thrown by `paginate()` when the supplied signal fires between
 * pages. Matches the `name === "AbortError"` shape every modern
 * runtime emits so callers can `if (err.name === "AbortError")` and
 * skip surfacing it.
 */
export class AsyncListAbortError extends Error {
  override readonly name = "AbortError";
  constructor() {
    super("aborted");
  }
}

const checkAbort = (signal: AbortLike | undefined) => {
  if (signal?.aborted) {
    throw new AsyncListAbortError();
  }
};

/**
 * Walk every page of a cursor-paginated endpoint and return the
 * flattened items. Throws on the first page error (no retry — that
 * lives one layer down, in apiFetch).
 */
export async function paginate<T, C = string>(
  fetchPage: FetchPage<T, C>,
  opts: PaginateOptions<T, C> = {},
): Promise<{ items: T[]; pagesFetched: number }> {
  const maxPages = opts.maxPages ?? DEFAULT_MAX_PAGES;
  const out: T[] = [];
  let cursor: C | null = null;
  let pages = 0;
  do {
    checkAbort(opts.signal);
    const page = await fetchPage(cursor, opts.signal);
    out.push(...page.items);
    pages++;
    opts.onPage?.(page);
    cursor = page.cursor;
  } while (cursor !== null && pages < maxPages);
  return { items: out, pagesFetched: pages };
}
